import type {PhRecPageFilters} from './PhRecommendationListLayout';
import type {FreeSoloValue} from '@wallaby-ui/document';

type DrpFilter = NonNullable<PhRecPageFilters['drpFilter']>;
type MedicationNameFilter = Pick<DrpFilter, 'medicationIds' | 'medicationUserInput'>;

/** The medication box's value of the DRP filter: the typed text, or the id of the one medication picked. */
export function getMedicationNameValue(drpFilter: MedicationNameFilter | undefined): string | FreeSoloValue | undefined {
  if (drpFilter?.medicationUserInput) return {freeSolo: drpFilter.medicationUserInput};
  const medicationId = drpFilter?.medicationIds?.[0];
  return medicationId != null ? String(medicationId) : undefined;
}

/** The DRP filter fields of the medication box's value, which is either one picked medication or typed text. */
export function toMedicationNameFilter(value: string | FreeSoloValue | undefined | null): MedicationNameFilter {
  if (value == null) return {medicationIds: undefined, medicationUserInput: undefined};
  if (typeof value !== 'string') return {medicationIds: undefined, medicationUserInput: value.freeSolo};
  return {medicationIds: [+value], medicationUserInput: undefined};
}

type LegacyDrpFilter = {medicationName_local?: string | FreeSoloValue; medicationAtcId?: number[]; manifestations?: string[]};

const LEGACY_DRP_KEYS = ['medicationName_local', 'medicationAtcId', 'manifestations'];

/**
 * The filter with the DRP keys the page used before PDW-393 renamed to the DTO's, and without `manifestations`, which
 * the department report's Show all link put beside `manifestation` and the list never sent. Only a page state kept in
 * the browser (local storage, a bookmarked URL) can still hold them, since the saved filters were migrated in the
 * database, and a key left over would be refused when the filter is saved.
 */
export function migrateLegacyDrpFilter(recommendation: PhRecPageFilters): PhRecPageFilters {
  const drpFilter = recommendation.drpFilter as (DrpFilter & LegacyDrpFilter) | undefined;
  if (drpFilter == null || !LEGACY_DRP_KEYS.some((key) => key in drpFilter)) return recommendation;
  const {medicationName_local, medicationAtcId, manifestations: _manifestations, ...rest} = drpFilter;
  return {
    ...recommendation,
    drpFilter: {
      ...rest,
      ...(medicationName_local != null ? toMedicationNameFilter(medicationName_local) : {}),
      ...(medicationAtcId != null ? {atcHierarchyIds: medicationAtcId.map(Number)} : {}),
    },
  };
}
